import React, { ReactNode, useState } from 'react';
import { cloneDeep, get, isNil, set } from 'lodash';
import {
  Condition,
  FinanceType,
  Origin,
  TradeInType,
} from '@upstart/harmony/lib/constants';
import { connect } from 'react-redux';
import { getResidualValueFromPercent } from '@upstart/harmony/lib/payment-utils';
import { parseCurrencyTruncated } from '@upstart/harmony/lib/form-utils';
import { isIncentiveProgramExpired } from '@upstart/harmony/lib/incentive-utils';
import * as changeLogHelper from '@upstart/harmony/lib/deskingoffer-changelog';
import { TradeIn } from '@upstart/harmony/lib/legos/dealTypes';
import { DeskingOffer } from '@upstart/harmony/lib/legos/deskingTypes';
import { MdOutlineWarningAmber, MdReply } from 'react-icons/md';
import { FAUX_TRADE_IN_ID } from '@upstart/harmony/lib/legos/constants';
import {
  Action,
  ModalType,
  amplitudeEventsInvoice,
} from 'util/amplitudeEvents';
import { sendData } from 'util/amplitude';
import { isMarketScanPaymentsEnabled } from '@upstart/harmony/lib/dealer-utils';
import useProtectionPlans from 'components/PencilEditor/useProtectionPlans';
import useFetchDealOwner from 'components/PencilEditor/useFetchDealOwner';
import Input, { PercentageInput, PriceInput, SliderInput } from '../Input';
import {
  ACCESSORIES_HEADER,
  ADD_ONS_BOSAK_HEADER,
  ADD_ONS_HEADER,
  fauxTradeId,
  PROTECTION_PLANS_HEADER,
  RATE_FACTOR,
} from '../../../util/constants';
import { getDealerCashRuleAppliedValue } from '../../../util/paymentUtils';
import {
  OfferDataTable,
  OfferDataTableRow,
} from '../../DataTable/OfferDataTable';
import Tooltip from '../../Tooltip';
import { isPresent } from '../../../util/typeUtils';
import ValueDisplay from '../ValueDisplay/ValueDisplay';
import ApplyToOtherOffersModal from '../ApplyToOtherOffersModal';
import { DeskingOfferPayment } from '../types';
import { FetchTradeInAppraisalButton } from './FetchTradeInAppraisalButton';
import ModalLabel, { ModalLabelProps } from './ModalLabel';
import { ExpandableDataRow } from './ExpandableRow';
import {
  calculateDefaultCapitalizedTax,
  getAppliedPrograms,
  getMrm,
  isOnePayLease,
} from './desking-offer-utils';
import { DeskingOfferCardProps } from './types';
import './DeskingOfferCard.scss';
import { formatRateWithPrecision } from '../../../util/format';
import { filterAndJoinStrings } from '../../../util/utils';
import { TradePayoffModalLabel } from './TradePayoffModalLabel';
import {
  getTradeInPayoffState,
  NO_PAYOFF_ADDED,
  TradeInStatusMapDeskingV2,
} from '../../../util/tradePayoffUtils';
import { RATE_PRECISION } from '../constants';
import { isTradeInTaxCreditAvailable } from './tradein-tax-credit-utils';

const MAX_VALUE = 999999;

function ApplyToOtherOffers(props: {
  offer: DeskingOffer;
  offers: DeskingOffer[];
  onSave: (selectedOfferIds: string[]) => void;
}) {
  const [isModalOpen, setModalOpen] = useState(false);

  return (
    <div className="apply-all-container">
      <button
        className="apply-all-button"
        type="button"
        onClick={() => setModalOpen(true)}
      >
        <MdReply style={{ transform: 'scaleX(-1)' }} />
      </button>
      <ApplyToOtherOffersModal
        offers={props.offers ?? []}
        onChange={selectedOfferIds => props.onSave(selectedOfferIds)}
        isOpen={isModalOpen}
        closeModal={() => setModalOpen(false)}
      />
    </div>
  );
}

type DeskingOfferDataTableProps = DeskingOfferCardProps & {
  localOfferState: DeskingOffer;
  setLocalOfferState: (fn: (offer: DeskingOffer) => any) => void;
  defaultResidualPercentage?: number;
  onFieldChange: (
    field: string,
    type?: 'price' | 'percentage' | 'number' | 'checkbox',
    maxValue?: number | null,
  ) => (value: any) => void;
  offerPayment?: DeskingOfferPayment;
  offerPymtCalcInProgMap: {
    [key: string]: number;
  };
  readOnlyMode: boolean;
};

const onKeyDown = (e: any) => {
  if (e.key === 'Enter') {
    e.preventDefault();
    e.target.blur(); // blurring the input triggers the onChange callback
  }
};

export const buildRows = (
  props: DeskingOfferDataTableProps & {
    hasExpiredIncentive: boolean;
    setHasExpiredIncentive: (input: boolean) => void;
    initialInvoiceValue: number;
    setInitialInvoiceValue: (value: number | null) => void;
    currentInvoiceValue: number;
    setCurrentInvoiceValue: (value: number | null) => void;
    protectionPlansTotalPrice: number | string;
  },
) => {
  const { dealer, protectionPlansTotalPrice } = props;

  const isMarketscanEnabled = isMarketScanPaymentsEnabled(dealer);

  const applyToOtherOffers = async (
    offerIds: string[],
    fieldValuePairs: [string, any][],
    shouldLog?: boolean,
  ) => {
    const updatedOffers = props.applyChangesToOffers(
      offerIds,
      fieldValuePairs,
      shouldLog,
    );
    await props.saveOffers(updatedOffers, true);
  };

  const renderValue = (
    label: string | ReactNode,
    value: number | ReactNode,
    valueType: 'price' | 'percentage' | 'number' | 'checkbox' = 'price',
    options?: {
      tooltip?: string;
      precision?: number;
    },
  ): OfferDataTableRow => {
    let adjustedValue;
    if (valueType === 'checkbox') {
      adjustedValue = value === true ? 'Yes' : 'No';
    } else if (['price', 'percentage', 'number'].includes(valueType)) {
      adjustedValue = value === '' ? 0 : value;
    } else {
      adjustedValue = value;
    }
    return {
      label: options?.tooltip ? (
        <div className="label-with-tooltip">
          {label}
          <Tooltip width={140} text={options.tooltip}>
            <i className="glyphicon glyphicon-info-sign" />
          </Tooltip>
        </div>
      ) : (
        label
      ),
      value:
        typeof adjustedValue === 'number' && options?.precision ? (
          <div className="offer-value-container">
            {Number(adjustedValue.toFixed(options?.precision))}
          </div>
        ) : (
          <div className="offer-value-container">
            {(valueType === 'price' || valueType === 'percentage') &&
            (typeof adjustedValue === 'number' ||
              typeof adjustedValue === 'undefined') ? (
              <ValueDisplay
                type={valueType}
                loading={!props.offerPayment}
                value={adjustedValue}
              />
            ) : (
              adjustedValue ?? 'N/A'
            )}
          </div>
        ),
    };
  };

  const renderModalRow = (
    type: ModalLabelProps['type'],
    label: string,
    value: number | ReactNode,
    options?: {
      valueType?: 'price' | 'percentage';
      sublabel?: ReactNode;
      modalProps?: Partial<ModalLabelProps>;
      sendAmplitudeEvent?: (events: ModalType, action: Action) => void;
    },
  ): OfferDataTableRow => {
    const onSave = (offer: DeskingOffer) => {
      if (!offer.data) {
        return;
      }
      const otherOffers = (props.offers ?? [])?.filter(
        otherOffer => otherOffer.id !== offer.id,
      );
      const otherOfferIdsOfSameType = otherOffers
        .filter(
          otherOffer =>
            otherOffer.data?.financeType === offer.data?.financeType,
        )
        .map(otherOffer => otherOffer.id);

      // For certain modal data, we need to apply updates to all offers of the same finance type
      switch (type) {
        case 'accessories': {
          // Accessories apply to all offers regardless of finance type
          applyToOtherOffers(
            otherOffers.map(otherOffer => otherOffer.id),
            [['selectedAccessories', offer.data?.selectedAccessories]],
          );
          break;
        }
        case 'protectionPlans': {
          applyToOtherOffers(otherOfferIdsOfSameType, [
            ['selectedBundles', offer.data.selectedBundles],
            ['selectedProducts', offer.data.selectedProducts],
            [
              `protectionPlanProductPriceOverrides`,
              offer.data.protectionPlanProductPriceOverrides,
            ],
          ]);
          break;
        }
        case 'dealerFees': {
          applyToOtherOffers(otherOfferIdsOfSameType, [
            ['fees', offer.data?.fees],
          ]);
          break;
        }
        case 'addOns':
          applyToOtherOffers(otherOfferIdsOfSameType, [
            ['addOns', offer.data?.addOns ?? []],
          ]);
          break;
        default:
          break;
      }
      set(offer, `sentToSalespersonAt`, null);
      if (props.sendAmplitudeEvent) {
        props.sendAmplitudeEvent(type, Action.FINISH);
      }
      props.onChange(offer);
    };

    return renderValue(
      <ModalLabel
        {...props}
        {...options?.modalProps}
        type={type}
        label={label}
        sublabel={options?.sublabel}
        onSave={onSave}
      />,
      value,
      options?.valueType,
    );
  };

  const renderInput = (
    label: ReactNode,
    field: string,
    type: 'price' | 'percentage' | 'number' | 'checkbox' = 'price',
    options?: {
      required?: boolean;
      unit?: string;
      defaultVal?: number | string | boolean | null;
      maxValueFieldName?: string | null;
      precision?: number;
      numberFormat?: (v: any) => string;
      numberParse?: (v: string) => any;
      hideApplyToOtherOffers?: boolean;
      disabled?: boolean;
      disableOnBulkUpdate?: boolean;
      allowApplyToAllOffers?: boolean;
    },
  ): OfferDataTableRow => {
    const value = get(
      props.localOfferState,
      `data.${field}`,
      options?.defaultVal ?? '',
    );

    // if rendering in read-only, render as a static value
    if (props.readOnlyMode) {
      return renderValue(label, value, type, { precision: options?.precision });
    }

    const isDisabled = props.disabled || options?.disabled;
    const disableOnBulkUpdate = options?.disableOnBulkUpdate;

    const isCurOfferPymtInProg = () =>
      (props.offerPymtCalcInProgMap?.[props?.offer?.id] || 0) > 0;
    const maxValue = options?.maxValueFieldName
      ? get(props.offer.data, options.maxValueFieldName, MAX_VALUE)
      : MAX_VALUE;

    const unit =
      options?.unit ??
      /* eslint-disable-next-line no-nested-ternary */
      (type === 'price' ? '$' : type === 'percentage' ? '%' : '');

    // Determine the input step size for default input
    let inputStep = type === 'percentage' ? '0.01' : '1';
    if (options?.precision && options.precision > 0) {
      inputStep = `0.${'0'.repeat(options.precision - 1)}1`;
    }

    const relevantOffers =
      (options?.allowApplyToAllOffers
        ? props.offers
        : props.offers?.filter(
            subOffer =>
              subOffer.data?.financeType === props.offer.data?.financeType,
          )) ?? [];

    const error = props.errors?.[field];
    return {
      label,
      value: (
        <div className="offer-input-container">
          {!error &&
            props.offer.data?.financeType !== FinanceType.CASH &&
            !options?.hideApplyToOtherOffers && (
              <ApplyToOtherOffers
                offer={props.offer}
                offers={relevantOffers}
                onSave={selectedOfferIds =>
                  applyToOtherOffers(selectedOfferIds, [[field, value]])
                }
              />
            )}
          <span className="input-unit">{unit}</span>
          {/* eslint-disable-next-line no-nested-ternary */}
          {type === 'price' ? (
            <PriceInput
              displayDollarSign={false}
              value={value}
              disabled={isDisabled}
              onChange={newValue => {
                props.setLocalOfferState(currentLocalOfferState => {
                  set(
                    currentLocalOfferState,
                    `data.${field}`,
                    parseCurrencyTruncated(newValue),
                  );
                  return { ...currentLocalOfferState };
                });
                if (field === 'invoice') {
                  // update the current invoice value
                  props.setCurrentInvoiceValue(newValue);
                }

                props.onFieldChange(field, type, maxValue)(newValue);
              }}
              onFocus={() => {
                if (field === 'invoice') {
                  sendData(
                    amplitudeEventsInvoice.DESKING_PENCIL_INVOICE_PRICE_START,
                  );
                }
              }}
              onBlur={() => {
                if (
                  field === 'invoice' &&
                  // check if there is any update to invoice value
                  props.initialInvoiceValue !== props.currentInvoiceValue
                ) {
                  // Update initial value
                  props.setInitialInvoiceValue(props.currentInvoiceValue);

                  sendData(
                    amplitudeEventsInvoice.DESKING_PENCIL_INVOICE_PRICE_FINISH,
                  );
                }
              }}
              onKeyDown={onKeyDown}
            />
          ) : // eslint-disable-next-line no-nested-ternary
          type === 'percentage' ? (
            <PercentageInput
              value={value}
              disabled={isDisabled}
              onChange={newValue => {
                // Set local state AND execute field change
                props.setLocalOfferState(currentLocalOfferState => {
                  set(
                    currentLocalOfferState,
                    `data.${field}`,
                    parseCurrencyTruncated(newValue),
                  );
                  return { ...currentLocalOfferState };
                });
                props.onFieldChange(field, type, maxValue)(newValue);
              }}
              displayPercentSign={false}
              step={inputStep}
              onKeyDown={onKeyDown}
            />
          ) : type === 'checkbox' ? (
            <Input
              type="checkbox"
              checked={value}
              disabled={
                isDisabled || (disableOnBulkUpdate && isCurOfferPymtInProg())
              }
              onChange={props.onFieldChange(field, type, maxValue)}
            />
          ) : (
            <Input
              {...(field === 'rate' ? { type } : {})}
              value={options?.numberParse?.(value) ?? value}
              disabled={isDisabled}
              onChange={(newValue: any) => {
                let formattedValue = newValue;
                if (options?.numberParse) {
                  formattedValue = options.numberParse(newValue);
                }
                props.setLocalOfferState(currentLocalOfferState => {
                  set(currentLocalOfferState, `data.${field}`, formattedValue);
                  return { ...currentLocalOfferState };
                });
                props.onFieldChange(field, type, maxValue)(formattedValue);
              }}
              step={inputStep}
              onKeyDown={onKeyDown}
            />
          )}
        </div>
      ),
      error,
    };
  };

  const renderTaxRow = (
    taxRate: number | null | undefined,
    taxAmount: number,
  ): OfferDataTableRow => {
    const label = (
      <ModalLabel
        {...props}
        type={ModalType.TAX_RATE}
        label="Tax"
        sublabel={<span style={{ marginLeft: 10 }}>Total Taxes</span>}
        onSave={offer => {
          const otherOfferIds = (props.offers ?? [])
            .filter(
              otherOffer =>
                otherOffer.id !== offer.id &&
                otherOffer.data?.financeType === offer.data?.financeType,
            )
            .map(otherOffer => otherOffer.id);
          applyToOtherOffers(otherOfferIds, [
            ['tax', offer.data?.tax],
            ['taxBreakdown', offer.data?.taxBreakdown],
          ]);
          set(offer, `sentToSalespersonAt`, null);
          props.onChange(offer);
        }}
      />
    );
    const value = (
      <div className="offer-value-container">
        <div className="offer-tax-values-container">
          <ValueDisplay
            type={'percentage'}
            loading={!props.offerPayment}
            value={taxRate ?? undefined}
          />
          <ValueDisplay
            type={'price'}
            loading={!props.offerPayment}
            value={taxAmount}
          />
        </div>
      </div>
    );
    return { label, value };
  };

  const renderCarPrice = (displayPrice: number): OfferDataTableRow => {
    const carPriceRows: OfferDataTableRow[] = [
      renderInput('Vehicle Price', 'price'),
      props.car?.condition === Condition.NEW
        ? renderInput('MSRP', 'msrp')
        : renderValue('MSRP', props.car?.msrp),
      props.car?.condition === Condition.NEW
        ? renderInput('Invoice', 'invoice')
        : renderInput('Book Value', 'bookValue'),
      renderInput('Cost', 'cost'),
    ];

    return {
      value: (
        <ExpandableDataRow
          label="Display Price"
          sublabel={
            <span style={{ fontStyle: 'italic' }}>
              (Vehicle Price - Dealer Cash)
            </span>
          }
          value={
            <ValueDisplay
              type={'price'}
              loading={!props.offerPayment}
              value={displayPrice}
            />
          }
          dataRows={carPriceRows}
        />
      ),
    };
  };

  const renderTradeIns = (
    netTrade: number,
    pencilTradeIns: TradeIn[],
  ): OfferDataTableRow | null => {
    const showAppraisedValuePull =
      props.dealer?.tradeInManagerType === TradeInType.VAUTO &&
      get(props.dealer.vauto, ['enabled', Origin.INSTORE]);

    const fauxTradeIn = {
      id: fauxTradeId,
      tradeACV: 0,
      tradeInAllowance: 0,
      payOff: 0,
    };

    if (!pencilTradeIns || pencilTradeIns.length === 0) {
      pencilTradeIns = [fauxTradeIn];
    }

    const getTradePayoffInput = (index: number) => {
      let labelEl: string | ReactNode = 'Trade Payoff';
      let isDisabled = false;
      if (props.dealer.featureConfig?.tradeEquity?.enabled) {
        const tradeIn = get(
          props.localOfferState,
          `data.pencilTradeIns.${index}`,
        );

        if (tradeIn && tradeIn.id !== FAUX_TRADE_IN_ID && !props.readOnlyMode) {
          const payoffState = getTradeInPayoffState(tradeIn);
          const payoffProps = TradeInStatusMapDeskingV2[payoffState];
          const payoffLabel = (
            <TradePayoffModalLabel
              payoffStatusProperties={payoffProps}
              payoffState={payoffState}
              tradeIn={tradeIn}
              dealer={props.dealer}
              deal={props.deal}
              tradeInIndex={index}
              updatePayoffAmount={props.onFieldChange(
                `pencilTradeIns.${index}.payOff`,
                'price',
              )}
            />
          );
          labelEl = payoffLabel;
          isDisabled = payoffProps.inputDisabled;
        } else {
          // there are no trade ins on the deal (just a faux trade in) - so show message and do not let user perform payoff lookup.
          labelEl = (
            <div>
              Trade Payoff{' '}
              <Tooltip width={250} text={NO_PAYOFF_ADDED}>
                <i className="glyphicon glyphicon-info-sign" />
              </Tooltip>
            </div>
          );
        }
      }

      return renderInput(labelEl, `pencilTradeIns.${index}.payOff`, 'price', {
        hideApplyToOtherOffers: true,
        disabled: isDisabled,
      });
    };

    const tradeInRows: OfferDataTableRow[] =
      pencilTradeIns.reduce(
        (allRows, tradeIn: TradeIn, index: number): OfferDataTableRow[] => {
          const rows = [
            renderInput(
              'Trade In Allowance',
              `pencilTradeIns.${index}.tradeInAllowance`,
              'price',
              { hideApplyToOtherOffers: true },
            ),
            getTradePayoffInput(index),
            renderInput(
              <div>
                Trade ACV
                {tradeIn.id !== 'faux-trade-in' &&
                  showAppraisedValuePull &&
                  !props.readOnlyMode && (
                    <FetchTradeInAppraisalButton
                      tradeIn={tradeIn}
                      dealId={props.deal.id}
                      onChange={props.onFieldChange(
                        `pencilTradeIns.${index}.tradeACV`,
                      )}
                    />
                  )}
              </div>,
              `pencilTradeIns.${index}.tradeACV`,
              'price',
              { hideApplyToOtherOffers: true },
            ),
          ];

          // apply tax credit logic
          const isTradeInTaxCreditAvailableResult = isTradeInTaxCreditAvailable(
            {
              dealer: props.dealer,
              deal: props.deal,
              offer: props.offer,
              financeType:
                props.offer.data?.financeType || FinanceType.PURCHASE,
            },
          );
          if (!isTradeInTaxCreditAvailableResult.isTradeInTaxCreditAvailable) {
            rows.splice(
              1,
              0,
              renderValue('Apply Tax Credit', false, 'number', {
                tooltip: `Trade-In Tax credit is not applicable in the state of ${isTradeInTaxCreditAvailableResult.state}`,
              }),
            );
          } else {
            rows.splice(
              1,
              0,
              renderInput(
                'Apply Tax Credit',
                `pencilTradeIns.${index}.tradeInTaxCredit`,
                'checkbox',
                {
                  hideApplyToOtherOffers: true,
                  disableOnBulkUpdate: true,
                  defaultVal:
                    props.dealer?.desking?.useTradeInAllowanceForTax?.enabled &&
                    props.car?.condition
                      ? get(
                          props.dealer,
                          `desking.useTradeInAllowanceForTax.${props.car?.condition}`,
                        )
                      : false,
                },
              ),
            );
          }

          const labelArray = [tradeIn.make?.name, tradeIn.model?.name];
          if (tradeIn.id && tradeIn.id !== 'faux-trade-in') {
            // 'faux-trade-in' signifies no tradein is present in the deal
            // In this case, sales person may still want
            // to add a trade allowance
            labelArray.push('VIN', tradeIn.id);
          }
          const tradeInLabel = labelArray.join(' ').trim();
          if (tradeInLabel) {
            allRows.push({
              value: <span className="trade-in-label">{tradeInLabel}</span>,
            });
          }
          allRows.push(...rows);

          return allRows;
        },
        [] as OfferDataTableRow[],
      ) ?? [];

    return tradeInRows.length > 0
      ? {
          value: (
            <ExpandableDataRow
              label="Net Trade"
              value={
                <ValueDisplay
                  type={'price'}
                  loading={!props.offerPayment}
                  value={netTrade}
                />
              }
              dataRows={tradeInRows}
            />
          ),
        }
      : null;
  };

  const renderDealerCashRuleInput = (
    label: string,
    dealerCash: number,
  ): OfferDataTableRow => ({
    value: (
      <div className="offer-dealer-cash-rule-input-container">
        <div className="offer-dealer-cash-rule-input-label">{label}</div>
        <div className="offer-dealer-cash-rule-input-slider">
          <SliderInput
            value={getDealerCashRuleAppliedValue(props.offer.data)}
            max={dealerCash}
            disabled={props.disabled || !dealerCash}
            onChange={props.onFieldChange('dealerCashRule')}
            onKeyDown={onKeyDown}
          />
        </div>
      </div>
    ),
  });

  const updateResidualFromIncentiveOffer = (
    newOffer: any,
    consolidatedOffer: any,
  ) => {
    const term =
      props.offer.data && 'months' in props.offer.data
        ? props.offer.data.months
        : 0;
    const mileage =
      props.offer.data && 'mileage' in props.offer.data
        ? props.offer.data.mileage
        : 0;

    // residuals in the offer are stored as percentages
    const offerResidualPercent = consolidatedOffer?.programs?.find(
      (program: any) => !!program.residuals,
    )?.residuals?.[`${term}`]?.[mileage];

    if (!offerResidualPercent) {
      return;
    }

    const newResidual = getResidualValueFromPercent(
      props.dealer,
      props.car,
      getMrm(props),
      offerResidualPercent,
      props.offer.data?.msrp ?? 0,
    );

    // change the residual if there is a match in the stored residuals, otherwise invalidate it
    let newChangeLog = changeLogHelper.updateOfferProperty(
      newOffer,
      'residual',
      isFinite(newResidual) ? newResidual : undefined,
    );
    set(newOffer, 'changeLog', newChangeLog);
    set(
      newOffer,
      ['data', 'residual'],
      isFinite(newResidual) ? newResidual : undefined,
    );
    newChangeLog = changeLogHelper.updateMileage(newOffer, mileage);
    set(newOffer, 'changeLog', newChangeLog);
    set(newOffer, ['data', 'mileage'], mileage);
  };

  const renderIncentives = (): OfferDataTableRow => {
    const onIncentivesChange = (consolidatedOffers: any[]) => {
      consolidatedOffers.forEach(({ offer: offerData, consolidated }) => {
        // EditIncentives component only works with V1 offers (those are stored as `data` prop in V2 offer)
        // The line finds the matching deskingOffer based on the offer `data` property.
        // The reference check is preferable here because `offerData` is the same object that we extracted from each desking offer (deskingOffer.data).
        const matchingOffer = props.offers?.find(
          ({ data }) => data === offerData,
        );
        if (!matchingOffer) return;
        const newOffer = cloneDeep(matchingOffer);
        if (props.offer.data?.financeType === FinanceType.PURCHASE) {
          // update apr
          if (
            consolidated.apr !== undefined &&
            consolidated.apr !== props.offer.data.apr
          ) {
            const newChangeLog = changeLogHelper.updateApr(
              newOffer,
              consolidated.apr,
            );
            set(newOffer, 'changeLog', newChangeLog);
            set(newOffer, ['data', 'apr'], consolidated.apr);
          }
        } else if (props.offer.data?.financeType === FinanceType.LEASE) {
          updateResidualFromIncentiveOffer(newOffer, consolidated);

          // update rate
          if (
            consolidated.apr !== undefined &&
            consolidated.apr !== props.offer.data.rate * RATE_FACTOR
          ) {
            const newChangeLog = changeLogHelper.updateRate(
              newOffer,
              consolidated.apr * RATE_FACTOR,
            );
            set(newOffer, 'changeLog', newChangeLog);
            set(newOffer, ['data', 'rate'], consolidated.apr * RATE_FACTOR);
          }
        }

        // update selectedCustomerTypeId
        if (consolidated.selectedCustomerTypeId !== undefined) {
          set(
            newOffer,
            ['data', 'selectedCustomerTypeId'],
            consolidated.selectedCustomerTypeId,
          );
        }

        let newChangeLog = changeLogHelper.updateRebate(
          newOffer,
          consolidated.cash,
        );
        set(newOffer, 'changeLog', newChangeLog);
        set(newOffer, ['data', 'rebate'], consolidated.cash);

        newChangeLog = changeLogHelper.updateDealerCash(
          newOffer,
          consolidated.dealerCash,
        );
        set(newOffer, 'changeLog', newChangeLog);
        set(newOffer, ['data', 'dealerCash'], consolidated.dealerCash);

        newChangeLog = changeLogHelper.updateIncentives(
          newOffer,
          consolidated.programs,
        );
        set(newOffer, 'changeLog', newChangeLog);
        set(newOffer, ['data', 'programs'], consolidated.programs);
        props.onChange(newOffer);
      });
    };

    let value: ReactNode;

    // first filter out programs that have no applied values
    // (MS Incentives can be selected but not applied due to compatibility rules)

    const appliedPrograms = getAppliedPrograms(props.offer.data?.programs);

    if (appliedPrograms.length === 0) {
      value = <div>No Incentives Applied</div>;
    } else {
      value = appliedPrograms.map((program: any) => {
        const isExpired = isIncentiveProgramExpired(program);
        if (isExpired && !props.hasExpiredIncentive) {
          props.setHasExpiredIncentive(true);
        }
        return (
          <div
            key={program.id}
            className={isExpired ? 'incentive-expired' : ''}
          >
            {!!isExpired && (
              <MdOutlineWarningAmber className="incentive-warning-icon" />
            )}
            {filterAndJoinStrings([program.source, program.description])}
          </div>
        );
      });
    }
    return renderModalRow(ModalType.INCENTIVES, 'Incentives', value, {
      modalProps: {
        onIncentivesChange,
        sendAmplitudeEvent: props.sendAmplitudeEvent,
      },
    });
  };

  const renderResidualPercentageRow = (): OfferDataTableRow => {
    const showResetToDefaultResidualPercentageButton =
      !props.readOnlyMode &&
      typeof props.defaultResidualPercentage !== 'undefined' &&
      props.defaultResidualPercentage / 100 !==
        (props.offer.data as any)?.residualPercentage;

    const label = (
      <div>
        <div>Residual (Percentage)</div>
        {showResetToDefaultResidualPercentageButton && (
          <div
            className="residuals-percentage-reset"
            onClick={() => {
              props.onFieldChange('residualPercentage')(
                (props.defaultResidualPercentage ?? 0) / 100,
              );
            }}
          >
            (Reset)
          </div>
        )}
      </div>
    );

    return renderInput(label, 'residualPercentage', 'percentage');
  };

  const renderCapitalizedCostGroup = (
    capitalizedCost: number,
    netCapCostReduction: number,
  ): OfferDataTableRow => {
    const showCapitalizedTax = get(props.dealer, 'desking.useCapitalizedTax');

    const capCostRows: OfferDataTableRow[] = [
      renderValue(
        'Gross Capitalized Cost',
        capitalizedCost + netCapCostReduction,
      ),
      renderValue('Cap Cost Reduction', netCapCostReduction),
      !isMarketscanEnabled
        ? renderInput(
            'Include Tax in Capitalized Cost',
            'includeTaxInCapCost',
            'checkbox',
          )
        : null,
      showCapitalizedTax
        ? renderInput('Capitalized Tax', 'capitalizedTaxOverride', 'price', {
            defaultVal: calculateDefaultCapitalizedTax({
              offer: props.offer,
              dealer: props.dealer,
            }),
          })
        : null,
    ].filter(isPresent);

    return {
      value: (
        <ExpandableDataRow
          label="Net Capitalized Cost"
          value={
            <ValueDisplay
              type={'price'}
              loading={!props.offerPayment}
              value={capitalizedCost}
            />
          }
          dataRows={capCostRows}
        />
      ),
    };
  };

  const renderOnePayLeaseToggle = (): OfferDataTableRow => {
    const value = isOnePayLease(props);
    if (props.readOnlyMode) {
      return renderValue('One Pay Lease', value, 'checkbox');
    }
    return {
      label: 'One Pay Lease',
      value: (
        <Input
          type="checkbox"
          checked={isOnePayLease(props)}
          disabled={props.disabled}
          onChange={() => props.toggleOnePayLease()}
        />
      ),
    };
  };

  // get the active offer with payments

  const {
    balance,
    capitalizedCost = 0,
    displayPrice = 0,
    financeAmount,
    gross,
    loanToValue,
    monthlyTax,
    netCapCostReduction = 0,
    netTrade = 0,
    onePayment,
    serviceFees,
    taxAmount = 0,
    totalAtStart,
  } = props.offerPayment ?? {};

  const addOns = props.offer.data?.addOns ?? [];
  const addOnsSum = addOns.reduce(
    (total: number, addOn: { price: number }) => total + addOn.price || 0,
    0,
  );

  const accessories = props.offer.data?.selectedAccessories ?? [];
  const accessoriesSum = (accessories ?? []).reduce(
    (total: number, addOn: { price: number }) => total + addOn.price || 0,
    0,
  );

  // REL-5717 Hackfix until we implement a proper translations microservice
  const ADD_ONS_LABEL = props.dealer.id.includes('bosak')
    ? ADD_ONS_BOSAK_HEADER
    : ADD_ONS_HEADER;

  const dealerFeesLabel =
    props.translations?.instore?.['en-US']?.global?.dealerFees || 'Dealer Fees';

  const shouldIgnoreMRMValue = get(props.dealer, 'desking.ignoreMRM');
  const taxRateToShow =
    !isNil(props.localOfferState.data?.taxBreakdown?.state?.amountOverride) ||
    !isNil(props.localOfferState.data?.taxBreakdown?.county?.amountOverride) ||
    !isNil(props.localOfferState.data?.taxBreakdown?.city?.amountOverride)
      ? null
      : props.localOfferState.data?.tax;

  const getPurchaseRows = (): OfferDataTableRow[] =>
    [
      renderCarPrice(displayPrice),
      renderInput('APR', 'apr', 'percentage'),
      renderInput('Rebate', 'rebate'),
      renderIncentives(),
      renderTaxRow(taxRateToShow, taxAmount),
      renderInput('Days to First Payment', 'daysToFirstPayment', 'number', {
        unit: 'days',
        numberParse: v => (isFinite(Number(v)) ? Number(v) : 0),
      }),
      renderInput('Down Payment', 'downPayment', 'price', {
        defaultVal: 9,
        maxValueFieldName: 'invoice',
      }),
      renderModalRow(
        ModalType.PROTECTION_PLANS,
        PROTECTION_PLANS_HEADER,
        protectionPlansTotalPrice,
      ),
      renderModalRow(ModalType.ADD_ONS, ADD_ONS_LABEL, addOnsSum),
      renderModalRow(ModalType.ACCESSORIES, ACCESSORIES_HEADER, accessoriesSum),
      renderTradeIns(
        netTrade,
        props.localOfferState.data?.pencilTradeIns ?? [],
      ),
      renderInput('Dealer Cash', 'dealerCash'),
      props.readOnlyMode
        ? null
        : renderDealerCashRuleInput(
            'Apply Dealer Cash',
            props.offer.data?.dealerCash ?? 0,
          ),
      renderInput('Discount', 'discount', 'price', {
        allowApplyToAllOffers: true,
      }),
      renderModalRow(ModalType.DEALER_FEES, dealerFeesLabel, serviceFees),
      renderValue('Amount Financed', financeAmount),
      renderValue('Loan To Value', loanToValue, 'percentage', {
        tooltip: 'LTV is calculated based on invoice/book value',
      }),
      renderInput('Pack', 'pack', 'price', { allowApplyToAllOffers: true }),
      renderValue('Front Gross', gross),
      renderInput('Freight', 'freight', 'price', {
        allowApplyToAllOffers: true,
      }),
      get(props.dealer, 'desking.enable10DollarMonthlyPriceRangeToggle')
        ? renderInput(
            'Show Price Range',
            'show10DollarMonthlyPriceRange',
            'checkbox',
            {
              defaultVal: get(
                props.dealer,
                'desking.show10DollarMonthlyPriceRangeByDefault',
              ),
              hideApplyToOtherOffers: true,
            },
          )
        : null,
    ].filter(isPresent);

  const getLeaseRows = (): OfferDataTableRow[] =>
    [
      renderCapitalizedCostGroup(capitalizedCost, netCapCostReduction),
      renderCarPrice(displayPrice),
      renderValue(
        isOnePayLease(props) ? 'Total Due' : 'Total at Start',
        isOnePayLease(props) ? onePayment : totalAtStart,
      ),
      !isOnePayLease(props)
        ? renderInput('Rate (Percentage)', 'ratePercentage', 'percentage')
        : null,
      !isOnePayLease(props)
        ? renderInput('Rate (Money Factor)', 'rate', 'number', {
            precision: RATE_PRECISION,
            numberParse: v => {
              const res = parseFloat(
                formatRateWithPrecision(v, RATE_PRECISION),
              );
              return !isNaN(res) ? res : null;
            },
          })
        : null,
      isOnePayLease(props)
        ? renderInput(
            'Rate (One Pay Percentage)',
            'onePayLeaseApr',
            'percentage',
          )
        : null,
      isOnePayLease(props)
        ? renderInput(
            'Rate (One Pay Money Factor)',
            'onePaymentRate',
            'number',
            {
              precision: RATE_PRECISION,
              numberParse: v => {
                const res = parseFloat(
                  formatRateWithPrecision(v, RATE_PRECISION),
                );
                return !isNaN(res) ? res : null;
              },
            },
          )
        : null,
      renderResidualPercentageRow(),
      renderInput('Residual', 'residual'),
      shouldIgnoreMRMValue
        ? {
            label: 'MRM',
            value: <div className="offer-value-container">N/A</div>,
          }
        : renderInput('MRM', 'mrm'),
      renderInput('Rebate', 'rebate'),
      renderIncentives(),
      renderTaxRow(taxRateToShow, taxAmount),
      renderValue('Monthly Taxes', monthlyTax),
      isOnePayLease(props)
        ? null
        : renderInput('Down Payment', 'downPayment', 'price', {
            defaultVal: 9,
            maxValueFieldName: 'invoice',
          }),
      renderModalRow(
        ModalType.PROTECTION_PLANS,
        PROTECTION_PLANS_HEADER,
        protectionPlansTotalPrice,
      ),
      renderModalRow(ModalType.ADD_ONS, ADD_ONS_LABEL, addOnsSum),
      renderModalRow(ModalType.ACCESSORIES, ACCESSORIES_HEADER, accessoriesSum),
      renderTradeIns(
        netTrade,
        props.localOfferState.data?.pencilTradeIns ?? [],
      ),
      renderInput('Dealer Cash', 'dealerCash'),
      props.readOnlyMode
        ? null
        : renderDealerCashRuleInput(
            'Apply Dealer Cash',
            props.offer.data?.dealerCash ?? 0,
          ),
      renderInput('Discount', 'discount', 'price', {
        allowApplyToAllOffers: true,
      }),
      renderModalRow(ModalType.DEALER_FEES, dealerFeesLabel, serviceFees),
      renderInput('Pack', 'pack', 'price', { allowApplyToAllOffers: true }),
      renderValue('Front Gross', gross),
      renderInput('Freight', 'freight', 'price', {
        allowApplyToAllOffers: true,
      }),
      get(props.dealer, 'desking.enable10DollarMonthlyPriceRangeToggle')
        ? renderInput(
            'Show Price Range',
            'show10DollarMonthlyPriceRange',
            'checkbox',
            {
              defaultVal: get(
                props.dealer,
                'desking.show10DollarMonthlyPriceRangeByDefault',
              ),
              hideApplyToOtherOffers: true,
            },
          )
        : null,
      dealer?.desking?.addInceptionFeesToTotalAtStart
        ? renderInput(
            'Add Inception Fees to Total at Start',
            'addInceptionFeesToTotalAtStart',
            'checkbox',
            { hideApplyToOtherOffers: true },
          )
        : null,
      get(props.dealer, 'desking.enableOnePayLease')
        ? renderOnePayLeaseToggle()
        : null,
    ].filter(isPresent);

  const getCashRows = (): OfferDataTableRow[] =>
    [
      renderCarPrice(displayPrice),
      renderInput('Rebate', 'rebate'),
      renderIncentives(),
      renderTaxRow(taxRateToShow, taxAmount),
      renderInput('Deposit', 'downPayment', 'price', {
        defaultVal: 9,
        maxValueFieldName: 'invoice',
      }),
      renderModalRow(
        ModalType.PROTECTION_PLANS,
        PROTECTION_PLANS_HEADER,
        protectionPlansTotalPrice,
      ),
      renderModalRow(ModalType.ADD_ONS, ADD_ONS_LABEL, addOnsSum),
      renderModalRow(ModalType.ACCESSORIES, ACCESSORIES_HEADER, accessoriesSum),
      renderTradeIns(
        netTrade,
        props.localOfferState.data?.pencilTradeIns ?? [],
      ),
      renderInput('Dealer Cash', 'dealerCash'),
      props.readOnlyMode
        ? null
        : renderDealerCashRuleInput(
            'Apply Dealer Cash',
            props.offer.data?.dealerCash ?? 0,
          ),
      renderInput('Discount', 'discount', 'price', {
        allowApplyToAllOffers: true,
      }),
      renderModalRow(ModalType.DEALER_FEES, dealerFeesLabel, serviceFees),
      renderInput('Pack', 'pack', 'price', { allowApplyToAllOffers: true }),
      renderValue('Front Gross', gross),
      renderInput('Freight', 'freight', 'price', {
        allowApplyToAllOffers: true,
      }),
      renderValue('Total Due (Balance)', balance),
    ].filter(isPresent);

  switch (props.offer.data?.financeType) {
    case FinanceType.PURCHASE:
      return getPurchaseRows();
    case FinanceType.LEASE:
      return getLeaseRows();
    case FinanceType.CASH:
      return getCashRows();
  }
  return [];
};

function DeskingOfferDataTable(props: DeskingOfferDataTableProps) {
  const [hasExpiredIncentive, setHasExpiredIncentive] = useState(false);
  // getting initial invoice value
  const invoiceValue = get(props.localOfferState, `data.invoice`, '');
  const [initialInvoiceValue, setInitialInvoiceValue] = useState(invoiceValue);
  const [currentInvoiceValue, setCurrentInvoiceValue] = useState(invoiceValue);
  const { car, deal, dealer, pencil, offer } = props;
  const { dealOwner } = useFetchDealOwner({ car, dealer });
  // Note: Move this logic inside the hook (same logic exist in ProtectionPlansModal)
  const offerTerm =
    offer.data != null && 'months' in offer.data
      ? offer.data.months
      : undefined;
  const offerMileage =
    offer.data != null && 'mileage' in offer.data
      ? offer.data.mileage
      : undefined;
  const isOnePay = !!isOnePayLease({
    offer,
    deal,
    dealer,
  });
  const {
    totalPrice: protectionPlansTotalPrice,
    fetching,
  } = useProtectionPlans({
    car,
    deal,
    dealer: dealOwner,
    financeType: offer.data?.financeType,
    pencil,
    selectedBundles: offer.data?.selectedBundles,
    selectedProducts: offer.data?.selectedProducts,
    protectionPlanProductPriceOverrides:
      offer.data?.protectionPlanProductPriceOverrides,
    activeFinanceTerm: offerTerm,
    selectedMileage: offerMileage,
    isOnePayLease: isOnePay,
  });

  const rows = buildRows({
    ...props,
    hasExpiredIncentive,
    setHasExpiredIncentive,
    initialInvoiceValue,
    setInitialInvoiceValue,
    currentInvoiceValue,
    setCurrentInvoiceValue,
    protectionPlansTotalPrice: fetching
      ? 'Fetching...'
      : protectionPlansTotalPrice,
  });

  return <OfferDataTable rows={rows} />;
}

const mapStateToProps = ({ deskingPayments }: any) => ({
  offerPymtCalcInProgMap: deskingPayments.paymentCalcInProgress,
});

export default connect(mapStateToProps)(DeskingOfferDataTable);
